import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { ArrowLeft, Calendar, TrendingUp, Scale, BarChart3, FileText } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from "recharts";

export default function CedantResult() {
  const navigate = useNavigate();
  const [result, setResult] = useState(null);

  useEffect(() => {
    const fetchResult = async () => {
      const token = localStorage.getItem("access_token");
      const userType = localStorage.getItem("user_type");

      if (token && userType === "cedant") {
        try {
          const response = await axios.get(`${API}/cedants/me/profile`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          setResult(response.data);
          return;
        } catch (err) {
          console.error("Error fetching cedant profile:", err);
          toast.error("Erreur lors de la récupération de vos données.");
        }
      }

      const stored = localStorage.getItem("cedant_result");
      if (stored) {
        setResult(JSON.parse(stored));
      } else {
        navigate("/cedant");
      }
    };

    fetchResult();
  }, [navigate]);

  if (!result) {
    return null;
  }

  const valuation = result.valuation_estimee;
  const chartData = [
    { name: "Patrimoniale", value: valuation.patrimoniale, color: "hsl(var(--primary))" },
    { name: "Rendement", value: valuation.rendement, color: "hsl(var(--accent))" },
    { name: "Comparative", value: valuation.comparative, color: "rgba(30, 63, 77, 0.4)" },
  ];

  const formatCurrency = (value) => {
    return new Intl.NumberFormat("fr-FR", {
      style: "currency",
      currency: "EUR",
      maximumFractionDigits: 0,
    }).format(value);
  };

  return (
    <div className="min-h-screen bg-background py-16 px-8" data-testid="cedant-result">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-16">
          <button
            onClick={() => navigate("/")}
            className="flex items-center gap-2 text-foreground/60 hover:text-primary transition-colors font-medium"
            data-testid="back-home"
          >
            <ArrowLeft className="w-5 h-5" />
            Retour à l'accueil
          </button>
          <span className="text-3xl font-bold text-primary">Sequenti</span>
        </div>

        {/* Success Message */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center justify-center w-24 h-24 bg-primary/10 rounded-full mb-8">
            <TrendingUp className="w-12 h-12 text-primary" />
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-primary mb-6">
            Votre estimation de valorisation
          </h1>
          <p className="text-xl text-foreground/60 max-w-2xl mx-auto">
            Bonjour <span className="text-primary font-bold">{result.prenom}</span>, voici une première estimation basée sur vos données.
          </p>
        </div>

        {/* Main Valuation Card */}
        <Card className="p-12 mb-12 bg-white border-none rounded-lg shadow-sm border border-border/40 text-center overflow-hidden relative">
            <div className="absolute top-0 left-0 w-full h-2 bg-accent" />
            <p className="text-sm font-bold uppercase tracking-widest text-foreground/40 mb-6">
              Fourchette de valorisation estimée
            </p>
            <div className="flex flex-col md:flex-row items-center justify-center gap-4 mb-8">
              <span className="text-3xl font-bold text-primary">
                {formatCurrency(valuation.fourchette_basse)}
              </span>
              <span className="text-foreground/20 text-2xl">—</span>
              <span className="text-3xl font-bold text-primary">
                {formatCurrency(valuation.fourchette_haute)}
              </span>
            </div>
            <div className="bg-muted py-8 px-12 rounded-lg inline-block">
                <p className="text-5xl md:text-7xl font-black text-accent mb-2">
                {formatCurrency(valuation.moyenne)}
                </p>
                <p className="text-lg font-semibold text-primary/60">Valeur moyenne estimée</p>
            </div>
        </Card>

        {/* Methods Breakdown */}
        <div className="grid md:grid-cols-3 gap-8 mb-12">
          <Card className="p-8 border-none bg-white shadow-sm border border-border/40 flex flex-col items-center text-center">
            <div className="w-14 h-14 bg-primary/10 rounded-full flex items-center justify-center mb-6">
                <Scale className="w-7 h-7 text-primary" />
            </div>
            <p className="text-sm font-bold uppercase tracking-wider text-foreground/40 mb-2">Méthode</p>
            <p className="text-xl font-bold text-primary mb-4">Patrimoniale</p>
            <p className="text-2xl font-black text-accent">{formatCurrency(valuation.patrimoniale)}</p>
            <p className="text-sm text-foreground/60 mt-4 leading-relaxed italic">
              Basée sur les actifs corporels, incorporels et le CA.
            </p>
          </Card>

          <Card className="p-8 border-none bg-white shadow-sm border border-border/40 flex flex-col items-center text-center">
            <div className="w-14 h-14 bg-primary/10 rounded-full flex items-center justify-center mb-6">
                <TrendingUp className="w-7 h-7 text-primary" />
            </div>
            <p className="text-sm font-bold uppercase tracking-wider text-foreground/40 mb-2">Méthode</p>
            <p className="text-xl font-bold text-primary mb-4">Rendement</p>
            <p className="text-2xl font-black text-accent">{formatCurrency(valuation.rendement)}</p>
            <p className="text-sm text-foreground/60 mt-4 leading-relaxed italic">
              Basée sur la rentabilité future capitalisée.
            </p>
          </Card>

          <Card className="p-8 border-none bg-white shadow-sm border border-border/40 flex flex-col items-center text-center">
            <div className="w-14 h-14 bg-primary/10 rounded-full flex items-center justify-center mb-6">
                <BarChart3 className="w-7 h-7 text-primary" />
            </div>
            <p className="text-sm font-bold uppercase tracking-wider text-foreground/40 mb-2">Méthode</p>
            <p className="text-xl font-bold text-primary mb-4">Comparative</p>
            <p className="text-2xl font-black text-accent">{formatCurrency(valuation.comparative)}</p>
            <p className="text-sm text-foreground/60 mt-4 leading-relaxed italic">
              Basée sur les multiples observés du marché.
            </p>
          </Card>
        </div>

        {/* Chart */}
        <Card className="p-10 mb-12 border-none bg-white shadow-sm border border-border/40">
          <h3 className="text-2xl font-bold text-primary mb-8">Comparaison des méthodes</h3>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData} layout="vertical" margin={{ left: 40, right: 40 }}>
                <CartesianGrid strokeDasharray="3 3" horizontal={true} vertical={false} stroke="hsl(var(--border))" />
                <XAxis 
                  type="number" 
                  tickFormatter={(value) => `${(value / 1000).toFixed(0)}k€`}
                  stroke="hsl(var(--muted-foreground))"
                  fontSize={12}
                />
                <YAxis 
                  type="category" 
                  dataKey="name" 
                  width={120} 
                  stroke="hsl(var(--muted-foreground))"
                  fontSize={14}
                  fontWeight={600}
                />
                <Tooltip 
                  cursor={{ fill: 'hsl(var(--muted))' }}
                  contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}
                  formatter={(value) => [formatCurrency(value), "Valeur"]}
                />
                <Bar dataKey="value" radius={[0, 8, 8, 0]} barSize={40}>
                  {chartData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>

        {/* Disclaimer */}
        <Card className="p-10 mb-12 bg-primary/5 border-none rounded-lg italic text-foreground/60 leading-relaxed border-l-4 border-accent">
          <p className="text-lg">
            <strong className="text-primary not-italic">Note importante :</strong> Cette estimation est indicative et basée sur les informations fournies. 
            Une valorisation précise nécessite une analyse approfondie de votre entreprise (due diligence, 
            audit comptable, analyse des contrats, etc.). Prenez rendez-vous avec Éric pour affiner cette estimation.
          </p>
        </Card>

        {/* CTA */}
        <div className="flex flex-col sm:flex-row gap-6 justify-center mt-16">
          <Button
            size="lg"
            className="bg-accent text-white hover:bg-accent/90 text-xl h-16 px-10 rounded-full shadow-lg transition-transform hover:scale-105"
            onClick={() => window.open("https://calendly.com/eric-sequenti/30min", "_blank")}
            data-testid="rdv-button"
          >
            <Calendar className="w-6 h-6 mr-3" />
            Prendre rendez-vous avec Éric
          </Button>
        </div>
      </div>
    </div>
  );
}
